/**
 * ONE shape for every workspace-scoped React Query key: `[name, workspaceId, ...rest]`.
 *
 * ## Why (steward-app#845)
 *
 * Workspace-scoped reads in this app used to be workspace-AGNOSTIC — `['assets']`,
 * `['documents']`, `['net-worth']` — so two workspaces shared one cache entry and the
 * only thing keeping one family's balances off another family's page was
 * `queryClient.resetQueries` firing at the right moment in `WorkspaceProvider`. #793
 * fixed the *timing* of that reset (a layout effect, so the stale frame is superseded
 * before it can be painted). It did not remove the reliance, and since #703/#693/#751
 * the other side of a switch is routinely a different client family.
 *
 * A read keyed on the workspace cannot serve the wrong workspace's data even if the
 * reset never runs, because the cache entry is a different entry. That is the property
 * this module exists to make structural: `WorkspaceProvider.keyedQueries.test.tsx`
 * stubs `resetQueries` out entirely and still finds no cross-family paint.
 *
 * ## Why a helper rather than a convention
 *
 * The failure mode this is guarding against is silent in both directions. A read keyed
 * `[name, wsId]` whose invalidation still says `[name]` over-invalidates (harmless); a
 * read keyed `[wsId, name]` or `[name, wsId, undefined]` silently misses its cache and
 * refetches forever; and a name that is scoped in one file and agnostic in another is
 * the original bug back again in one place. Nothing about a bare array literal fails
 * when it drifts. So the shape lives in one function, the set of scoped names is a
 * closed union {@link WorkspaceScopedQueryName} the compiler checks, and
 * `workspaceQueryKey.test.ts` greps the tree for any scoped name still written as a raw
 * string-literal key.
 *
 * ## ⚠️ The second argument is the workspace the DATA belongs to
 *
 * That is `activeWorkspaceId` from `useWorkspace()` almost everywhere, because almost
 * every read goes out under the ambient `X-Workspace-Id`. It is NOT `activeWorkspaceId`
 * on the advisor portal's cross-workspace reads, which pass a per-request workspace
 * override: `advisor-client-net-worth` belongs to the client family, and
 * `advisor-intakes` to the firm workspace, while the active workspace may be neither.
 * Keying those to the active workspace would file one workspace's data under another's
 * name — wrong in both directions. Pass the workspace whose data is coming back.
 *
 * `useWorkspaceScopedState` in `src/workspace/` carries the same warning for the other
 * half of the problem (page-local React state, #851). The two mechanisms are separate
 * and neither subsumes the other: this one keys the cache, that one keys component
 * state, and a workspace switch can leak through either.
 *
 * ## `null` is a key, not a hole
 *
 * `workspaceId` is `string | null` and `null` is kept as-is rather than collapsed to
 * `''`, for the reason `useWorkspaceScopedState` gives: a collapse is the one construct
 * capable of making two distinct states share a key, and `string | null` compares
 * perfectly well with `===`.
 *
 * A call site should nonetheless not FETCH under a null workspace. Every converted read
 * carries `enabled: … && !!activeWorkspaceId`, so the `[name, null]` entry is never
 * populated: a fetch made before `/me` resolves would go out under the façade's fallback
 * workspace and be cached under a key that a *different* user, signing in later in the
 * same tab, would read (the `QueryClient` in `main.tsx` is module-scoped and is never
 * cleared on sign-out). A disabled query reads as `loading` through
 * `queryDisplayState`, never as `empty`, so the cost is a held surface rather than a
 * wrong claim about content.
 */

/**
 * Every query name whose data belongs to one workspace.
 *
 * Adding a scoped read means adding its name here — the union is what makes
 * {@link workspaceKey} reject a typo, and it is the list the raw-literal grep test walks.
 * Do NOT add an identity-scoped name (`me`, `me/logins`, `me/factors`) or a name already
 * scoped to something other than a workspace (`advisor-firms`, `advisor-clients`,
 * `advisor-summary`, `advisor-pending` are all `/me`-derived and keyed on the acting firm):
 * those reads answer the same thing in every workspace, and keying them would refetch them
 * on every switch for nothing.
 */
export const WORKSPACE_SCOPED_QUERY_NAMES = [
  // Portfolio
  'accounts',
  'assets',
  'liabilities',
  // steward-app#254 — one liability's balance history. Third key segment is the liability id.
  'liability-balances',
  // steward-app#2568 — one asset's history events. Third key segment is the asset id.
  'asset-events',
  'entities',
  'contacts',
  // The household roster (steward-app#1425). Workspace-scoped like every other pillar
  // read: a person record is one family's identity data and must never survive a switch.
  'people',
  'valuations',
  'asset-lots',
  // Dashboard / net worth
  'net-worth',
  'net-worth-history',
  'net-worth-baseline',
  'dashboard-summary',
  // Documents and folders
  'documents',
  'document-versions',
  'folders',
  'folder-acl',
  'folder-visibility',
  'links',
  // Credentials
  'credentials',
  'vaults',
  'vault-acl',
  // Cash flow setup (steward-app#2592): the agreement row, one household's choices.
  'cash-flow-agreement',
  // Cash flow boards (steward-app#2593): the summary, and one owner's summary (third segment is the key).
  'cash-flow-summary',
  'cash-flow-entity',
  // Cash flow Activity (steward-app#2594): one month's transactions, third segment the month, fourth the account filter (or null).
  'cash-flow-transactions',
  // Cash flow Transfers tab and Worth a look (steward-app#2595): `GET /cash-flow/transfers`
  // (third segment is the month) and `GET /cash-flow/questions`. `CashFlowTransfers.tsx`.
  'cash-flow-transfers',
  'cash-flow-questions',
  // Membership
  'members',
  'invites',
  // Connections and billing
  'connections',
  'connection-removal-preview',
  // Kept bank connections the caller can bring into THIS workspace (`GET /connections/kept`).
  'kept-connections',
  'billing-config',
  // Assistant, feedback, capabilities, quota
  'assistant-threads',
  'assistant-config',
  'feedback',
  // steward-app#2612 — the announcements history; the audience appcore returns differs per workspace.
  'announcements',
  'capabilities',
  'workspace-quota',
  // Advisor firm channel on THIS family's own workspace (steward-app#1185) — which firms
  // can read the ACTIVE workspace's finances, and what it shares with each. Both are keyed
  // on `activeWorkspaceId` like every other read in this block, unlike the "belongs to a
  // workspace OTHER than the active one" group just below.
  'advisor-connections',
  'advisor-shared-areas',
  // crossmarc/steward-app#2439 — which users a firm's shared folder's ACL names as that firm's
  // advisors, for the "Shared by «firm»" caption (`useSharedByFirm.ts`).
  'shared-folder-advisors',
  // steward-app#1426 — the onboarding requests firms have made to the ACTIVE workspace.
  'onboarding-requests',
  // Advisor portal reads that belong to a workspace OTHER than the active one — see the
  // second-argument warning above.
  'advisor-client-net-worth',
  // steward-app#1426 — one client's onboarding facts, read for the ACTING firm. The third key
  // segment is that firm id, so a tab acting as another firm never reads this cache entry.
  'advisor-client-onboarding',
  'advisor-intakes',
  'advisor-intake',
  // steward-app#1543 part (b) — sponsored client setup WITHOUT a submitted intake. The
  // third key segment is the acting firm id, same convention as `advisor-intakes`.
  'advisor-sponsored-invitations',
  // steward-app#1634 — the Prospects tab's "Invitations sent" list. Same convention again:
  // third key segment is the acting firm id.
  'advisor-intake-invitations',
  // steward-app#1764 — the firm's "invite straight to onboarding" rows. Third key segment is
  // the acting firm id, as above (`advisor/onboardingInvitations.ts`).
  'advisor-onboarding-invitations',
  // steward-app#1763 — the Clients tab's `advisor-client` list, and the client profile
  // page's single-record read. Same acting-firm-in-the-key convention as the two above;
  // `advisor-client-record` (singular) carries the `clientId` as an additional key segment
  // ahead of the acting firm id — see `useClientRecords.ts`.
  'advisor-client-records',
  'advisor-client-record',
  // steward-app#1772 — who is assigned to one client (the profile's Advisors panel). Same
  // shape as `advisor-client-record`: `clientId`, then the acting firm id.
  'advisor-client-advisors',
  // crossmarc/steward-app#1770 S5 — the firm's client deletion records (admin only). Then the
  // acting firm id.
  'advisor-client-purges',
  // crossmarc/steward-app#1866 — the Clients tab's Archived view (clients removed with Remove
  // client). Same shape as `advisor-client-records`: the acting firm id. `useClientArchives.ts`.
  'advisor-client-archives',
  // steward-app#1782 — every client's advisors in one read (the Clients and Prospects
  // tables' Advisor column). Same shape as `advisor-client-records`: the acting firm id.
  'advisor-client-assignments',
  // steward-app#1768, docs/35 R3 — the client profile's About tab. Same shape again:
  // `clientId`, then the acting firm id.
  'advisor-client-about',
  // steward-app#1765 — the client profile's Onboarding details tab (the client's form answers).
  // Not `advisor-client-onboarding` above, which is #1426's onboarding facts. Same shape as
  // `advisor-client-about`: `clientId`, then the acting firm id.
  'advisor-client-onboarding-details',
  // docs/43 slice 5 — the firm's read of one client's answers (`?form=snapshot|onboarding`), which
  // the Snapshot and Onboarding tabs read. `clientId`, `form`, then the acting firm id.
  // `useClientAnswers.ts`.
  'advisor-client-answers',
  // docs/43 slice 9 (crossmarc/steward-app#2538) — the firm's masked view of a client's SSNs (subject
  // and last four). `clientId`, then the acting firm id. `staffIdentities.ts`.
  'advisor-client-identities',
  // steward-app#1769, docs/35 — the client profile's Firm documents tab. Same shape again:
  // `clientId`, then the acting firm id.
  'advisor-client-documents',
  'advisor-client-requests',
  // crossmarc/steward-app#1766 part 2, docs/35 R4 — who marked this client onboarded, and
  // when. Same shape again: `clientId`, then the acting firm id.
  'advisor-client-onboarding-mark',
  // crossmarc/steward-app#2550: one client's Close form / Reopen form state. Then the client id
  // and the acting firm id.
  'advisor-client-form-closures',
] as const;

export type WorkspaceScopedQueryName = (typeof WORKSPACE_SCOPED_QUERY_NAMES)[number];

/**
 * The scoped reads a completed account sync (or a connection added/removed) makes stale.
 *
 * Lives here rather than beside its consumers because it is a list of scoped NAMES, and
 * the whole point of this module is that the names appear as literals in exactly one file
 * — `workspaceQueryKey.test.ts` fails the build on a scoped name written as a raw string
 * literal anywhere else. `useConnectAccount.tsx` re-exports it as `SYNCED_DATA_KEYS` for
 * its existing callers.
 */
export const SYNCED_DATA_QUERY_NAMES = [
  'accounts', 'assets', 'liabilities', 'liability-balances', 'valuations', 'net-worth', 'dashboard-summary',
] as const satisfies readonly WorkspaceScopedQueryName[];

/**
 * Everything a connect, disconnect or sync can change: the synced data above, plus the two
 * reads the sync itself does not write — the connection list, and the plan's connection
 * count in `/billing/config`. Same reason as {@link SYNCED_DATA_QUERY_NAMES} for living
 * here; `useConnectAccount.tsx` re-exports it as `INVALIDATED_AFTER_CONNECT`.
 */
export const CONNECTION_QUERY_NAMES = [
  'connections', 'billing-config', ...SYNCED_DATA_QUERY_NAMES,
] as const satisfies readonly WorkspaceScopedQueryName[];

/**
 * The React Query key for one workspace's copy of `name`.
 *
 * ```ts
 * const { activeWorkspaceId } = useWorkspace();
 * useQuery({
 *   queryKey: workspaceKey('assets', activeWorkspaceId),
 *   queryFn: …,
 *   enabled: !!token && !!activeWorkspaceId,
 * });
 * ```
 *
 * Invalidation uses the SAME call, never a re-typed literal:
 * `invalidateQueries({ queryKey: workspaceKey('assets', activeWorkspaceId) })`. React
 * Query matches by prefix, so a stray `['assets']` would still invalidate — it would just
 * also drop every OTHER workspace's cached copy, refetching them on the next visit. The
 * grep test exists so that stops being a thing anyone has to notice in review.
 *
 * `rest` carries whatever already sub-divided the key (a document id, a probed
 * capability). It goes AFTER the workspace id so every scoped key reads
 * `[what, whose, which]` and `queryKey[1]` is always the workspace.
 */
export function workspaceKey(
  name: WorkspaceScopedQueryName,
  workspaceId: string | null,
  ...rest: readonly unknown[]
): readonly unknown[] {
  return [name, workspaceId, ...rest];
}
